import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { Drawer, PageHeader, SearchEmpty, SimpleToast } from "components";

import { updateCustomerCertificateAction } from "store/modules/customerCertificate/actions";
import { useAppDispatch, useAppSelector } from "store/rootReducer";

import { stringFormat } from "utils/stringFormat";

import {
  handleApplyButton,
  onLoadList,
  returnHtml,
  textAreaComponente,
} from "./constants";
import { ImportCertificateSkeleton } from "./ImportCertificateSkeleton";
import {
  ICustomerCertificateGetResponse,
  IImportCertificate,
} from "./interface";
import { toastOption } from "../../global/types";
import { LoadSpinner } from "../LoadSpinner";

export const ImportCertificate: React.FC<IImportCertificate> = ({
  title,
  projectName,
  api,
  permissions,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState<string>("");
  const [error, setError] = useState<boolean>(false);

  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [hasCertificate, setHasCertificate] = useState<boolean>(false);
  const [modality, setModality] = useState<string>("");
  const [amount, setAmount] = useState<string>("");
  const [extraAmount, setExtraAmount] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [finishDate, setFinishDate] = useState<string>("");
  const [limitRecord, setLimitRecord] = useState<number | null | undefined>(
    null,
  );
  const [certificateType, setCertificateType] = useState<
    number | null | undefined
  >(null);
  const [roomLimit, setRoomLimit] = useState<number | null | undefined>(null);
  //Toast
  const [toastTrigger, setToastTrigger] = useState<number>(0);
  const [toastBody, setToastBody] = useState<string>("");
  const [toastType, setToastType] = useState<toastOption>("warning");
  const [loading, setLoading] = useState(false);

  const [toastTriggerForm, setToastTriggerForm] = useState<number>(0);
  const [toastBodyForm, setToastBodyForm] = useState<string>("");
  const [toastTypeForm, setToastTypeForm] = useState<toastOption>("warning");
  const [loadingFinished, setLoadingFinished] = useState(false);

  const [updatePage, setUpdatePage] = useState<number>(0);

  const dispatch = useAppDispatch();

  const { user } = useAppSelector((state) => state.user);

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setValue("");
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setLoadingFinished(false);
      const customerId = user?.customerId;

      if (customerId) {
        const response = await onLoadList(customerId);

        if (response) {
          if (response.ok) {
            const certificate = response as ICustomerCertificateGetResponse;
            dispatch(updateCustomerCertificateAction(certificate.data));
            setAmount(certificate.data.amount);
            setExtraAmount(certificate.data.extraAmount);
            setModality(certificate.data.description);
            setStartDate(certificate.data.startDate);
            setFinishDate(certificate.data.finishDate);
            setLimitRecord(certificate.data.limitRecord);
            setCertificateType(certificate.data.certificateType);
            setHasCertificate(true);
            setRoomLimit(certificate.data.roomLimit);
          } else {
            setHasCertificate(false);
          }
        }
      } else {
        setHasCertificate(false);
      }
      setLoading(false);
      setLoadingFinished(true);
    };

    fetchData();
  }, [updatePage, api, user?.customerId]);

  const handleImportCertificate = () => {
    setOpenDrawer(true);
    setValue("");
    setError(false);
    setToastTrigger(0);
    setToastTriggerForm(0);
  };

  const handleApplyImportCertificate = async () => {
    if (!value || stringFormat.isEmptyOrSpaces(value.trim())) {
      setToastBodyForm(t("auth.certificate.fillField"));
      setToastTypeForm("warning");
      setError(true);
      setToastTriggerForm(toastTriggerForm + 1);
      return;
    } else {
      const returnPost = await handleApplyButton(api, value);

      if (returnPost) {
        if (returnPost.ok) {
          setToastType("success");
          setToastBody(returnPost.message);
          setError(false);
          setOpenDrawer(false);
          setToastTrigger(toastTrigger + 1);
          setUpdatePage(updatePage + 1);
        } else {
          setToastBodyForm(returnPost.message);
          setToastTypeForm("error");
          setError(true);
          setToastTriggerForm(toastTriggerForm + 1);
          return;
        }
      }
    }
  };

  return (
    <>
      <SimpleToast
        id="attendantRoleToastId"
        title={title}
        type={toastType}
        description={toastBody}
        handleCloseToast={() => {}}
        trigger={toastTrigger}
      />

      <PageHeader
        quantity={0}
        id="headerAdminArea"
        label={title}
        background="secondary"
        addClick={permissions.toCreate ? handleImportCertificate : undefined}
      />

      {loading ? (
        <ImportCertificateSkeleton />
      ) : permissions.toRead && loadingFinished && !hasCertificate ? (
        <SearchEmpty
          textMessage={
            permissions.toRead
              ? t("auth.certificate.notFound")
              : t("common.noPermission")
          }
          typeIcon="file"
          marginTop="30px"
        />
      ) : permissions.toRead && hasCertificate && !loading ? (
        returnHtml(
          t,
          projectName,
          modality,
          amount,
          extraAmount,
          startDate,
          finishDate,
          limitRecord,
          certificateType,
          roomLimit,
        )
      ) : null}

      <Drawer
        title={t("auth.certificate.importTitle", {
          title: title.toLocaleLowerCase(),
        })}
        open={openDrawer}
        onClose={handleCloseDrawer}
        onApply={handleApplyImportCertificate}
        paddingLeft={30}
        paddingRight={30}
      >
        {textAreaComponente(
          t("auth.certificate.title"),
          value,
          setValue,
          error,
          toastTypeForm,
          toastBodyForm,
          toastTriggerForm,
          "calc(100vh - 300px)",
        )}
      </Drawer>
      <LoadSpinner loading={loading} zindex={1300} />
    </>
  );
};
